import React, { useState, useEffect } from 'react'
import { Modal, Form, Button, Row, Col } from 'react-bootstrap'
import {
  Organization,
  UpdateOrganizationReq,
  useUpdateOrganizationMutation,
  useUploadOrganizationBrandingMutation,
  useGetOrganizationBrandingPublicQuery,
  useGetOrganizationPublicQuery,
} from '../../../services/orgsApi'
import { useGetUsersQuery } from '../../../services/userApi'
import { User } from '../../../types/models/user'
import styled from 'styled-components'

const StyledModal = styled(Modal)`
  .modal-content {
    background: #1a1a1a;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    color: white;
  }

  .modal-header {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .modal-footer {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .form-label {
    color: rgba(255, 255, 255, 0.8);
  }

  .form-control,
  .form-select {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: white;

    &:focus {
      background: rgba(255, 255, 255, 0.1);
      border-color: var(--bs-primary);
      color: white;
      box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25);
    }
  }

  .form-select {
    option {
      background: #1a1a1a;
      color: white;
    }
  }

  .form-control[type='file'] {
    &::file-selector-button {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: white;
      margin-right: 1rem;
      padding: 0.375rem 0.75rem;
      border-radius: 0.25rem;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
        border-color: rgba(255, 255, 255, 0.3);
      }
    }
  }
`

const BrandingThumbnail = styled.div`
  width: 120px;
  height: 80px;
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.1);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 6px;
  }

  .placeholder {
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
    text-align: center;
    padding: 0.5rem;
  }
`

const HiddenFileInput = styled.input`
  display: none;
`

interface EditOrganizationModalProps {
  show: boolean
  onHide: () => void
  organization: Organization | null
}

const EditOrganizationModal: React.FC<EditOrganizationModalProps> = ({
  show,
  onHide,
  organization,
}) => {
  const [updateOrganization] = useUpdateOrganizationMutation()
  const [updateOrganizationBranding] = useUploadOrganizationBrandingMutation()
  const { data: usersData } = useGetUsersQuery({ limit: 1000 })
  const { data: currentBranding, refetch: refetchBranding } =
    useGetOrganizationBrandingPublicQuery()
  const { refetch: refetchOrganization } = useGetOrganizationPublicQuery()
  const [formData, setFormData] = useState<UpdateOrganizationReq>({
    name: '',
    primary_poc: '',
    secondary_poc: '',
  })
  const [brandingFile, setBrandingFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Filter users by organization
  const organizationUsers =
    usersData?.users?.filter((user: User) => user.org_id === organization?.id) || []

  // Get selected users for email display
  const primaryUser = organizationUsers.find((user: User) => user.id === formData.primary_poc)
  const secondaryUser = organizationUsers.find((user: User) => user.id === formData.secondary_poc)

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        primary_poc: organization.primary_poc || '',
        secondary_poc: organization.secondary_poc || '',
      })
    }
  }, [organization])

  // Consolidated preview URL management
  useEffect(() => {
    // Reset preview when modal opens/closes
    if (!show) {
      setBrandingFile(null)
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }
      return
    }

    // Set current branding as preview when modal opens (only if no file selected and no preview set)
    if (show && currentBranding && !brandingFile && !previewUrl) {
      const url = URL.createObjectURL(currentBranding)
      setPreviewUrl(url)
    }
  }, [show, currentBranding, brandingFile]) // Removed previewUrl from dependencies

  // Handle file selection preview
  useEffect(() => {
    if (brandingFile) {
      // Clean up previous preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
      const url = URL.createObjectURL(brandingFile)
      setPreviewUrl(url)
    }
  }, [brandingFile]) // Removed previewUrl from dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, []) // Empty dependency array for cleanup only

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setBrandingFile(file)
    }
  }

  const handleThumbnailClick = () => {
    fileInputRef.current?.click()
  }

  const handleSubmit = async () => {
    if (!organization) return

    setIsLoading(true)
    try {
      // Update branding if a new file was selected
      if (brandingFile) {
        await updateOrganizationBranding(brandingFile).unwrap()
        // Manually refetch the branding to update the UI immediately
        await refetchBranding()
      }
      // Update organization data
      await updateOrganization({
        ...formData,
      }).unwrap()
      // Manually refetch the organization to update the UI immediately
      await refetchOrganization()

      onHide()
      // Reset form
      setFormData({
        name: '',
        primary_poc: '',
        secondary_poc: '',
      })
      setBrandingFile(null)
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }
    } catch (error) {
      console.error('Failed to update organization:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    onHide()
    // Reset form
    setFormData({
      name: '',
      primary_poc: '',
      secondary_poc: '',
    })
    setBrandingFile(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
  }

  return (
    <StyledModal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Organization</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Organization Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter organization name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Branding Image</Form.Label>
                <BrandingThumbnail onClick={handleThumbnailClick}>
                  {previewUrl ? (
                    <img src={previewUrl} alt="Branding preview" />
                  ) : (
                    <div className="placeholder">
                      Click to select
                      <br />
                      branding image
                    </div>
                  )}
                </BrandingThumbnail>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Primary Point of Contact</Form.Label>
                <Form.Select
                  value={formData.primary_poc || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      primary_poc: e.target.value || undefined,
                    })
                  }
                >
                  <option value="">Select Primary POC</option>
                  {organizationUsers.map((user: User) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.username})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Secondary Point of Contact</Form.Label>
                <Form.Select
                  value={formData.secondary_poc || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      secondary_poc: e.target.value || undefined,
                    })
                  }
                >
                  <option value="">Select Secondary POC</option>
                  {organizationUsers.map((user: User) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.username})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Primary POC Email</Form.Label>
                <Form.Control
                  type="email"
                  value={primaryUser?.email || ''}
                  readOnly
                  placeholder="Select a primary POC to see email"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Secondary POC Email</Form.Label>
                <Form.Control
                  type="email"
                  value={secondaryUser?.email || ''}
                  readOnly
                  placeholder="Select a secondary POC to see email"
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </StyledModal>
  )
}

export default EditOrganizationModal
